/**
 * A CSV reader for membership exports, written here rather than pulled in as a dependency.
 *
 * It follows RFC 4180, which is what spreadsheet programs write: fields separated by commas, a field in double quotes
 * may contain commas, line breaks and doubled quotes, and lines end in LF or CRLF. It also drops a leading byte-order
 * mark, which Excel adds to "CSV UTF-8" exports and which would otherwise corrupt the first header.
 */
export function parseCsv(text: string): string[][] {
  const input = text.startsWith("﻿") ? text.slice(1) : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  let i = 0;

  const endField = () => {
    row.push(field);
    field = "";
  };
  const endRow = () => {
    endField();
    // A blank line between rows is not a row of one empty field.
    if (!(row.length === 1 && row[0] === "")) rows.push(row);
    row = [];
  };

  while (i < input.length) {
    const char = input[i] as string;
    if (quoted) {
      if (char === '"') {
        if (input[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        quoted = false;
        i += 1;
        continue;
      }
      field += char;
      i += 1;
      continue;
    }
    if (char === '"' && field.length === 0) {
      quoted = true;
    } else if (char === ",") {
      endField();
    } else if (char === "\n") {
      endRow();
    } else if (char === "\r") {
      if (input[i + 1] === "\n") i += 1;
      endRow();
    } else {
      field += char;
    }
    i += 1;
  }
  if (field.length > 0 || row.length > 0) endRow();
  return rows;
}

/** The fields an import can fill, in the order the mapping screen offers them. */
export const IMPORT_FIELDS = [
  { key: "first_name", label: "First name", required: true },
  { key: "last_name", label: "Last name", required: true },
  { key: "preferred_name", label: "Goes by", required: false },
  { key: "birthdate", label: "Birthdate", required: false },
  { key: "email", label: "Email", required: false },
  { key: "phone", label: "Phone", required: false },
  { key: "household_name", label: "Household", required: false },
  { key: "address_line1", label: "Address", required: false },
  { key: "locality", label: "City", required: false },
  { key: "region", label: "State", required: false },
  { key: "postal_code", label: "ZIP code", required: false },
  { key: "tags", label: "Tags", required: false },
] as const;

export type ImportField = (typeof IMPORT_FIELDS)[number]["key"];

/** Header spellings seen in real exports, so most files map themselves. */
const ALIASES: Record<ImportField, readonly string[]> = {
  first_name: ["first name", "firstname", "first", "given name", "forename"],
  last_name: ["last name", "lastname", "last", "surname", "family name"],
  preferred_name: ["preferred name", "nickname", "goes by", "known as"],
  birthdate: ["birthdate", "birth date", "date of birth", "dob", "birthday"],
  email: ["email", "e-mail", "email address", "primary email"],
  phone: ["phone", "mobile", "cell", "phone number", "mobile phone", "cell phone"],
  household_name: ["household", "family", "household name", "family name household"],
  address_line1: ["address", "street", "address 1", "address line 1", "street address"],
  locality: ["city", "town"],
  region: ["state", "province", "region"],
  postal_code: ["zip", "zip code", "postal code", "postcode"],
  tags: ["tags", "tag", "groups", "labels"],
};

const normalise = (header: string) =>
  header.trim().toLowerCase().replace(/[_.]+/g, " ").replace(/\s+/g, " ");

/** Proposes a column for each field from the headers; staff can change any of them before staging. */
export function guessMapping(headers: readonly string[]): Partial<Record<ImportField, number>> {
  const mapping: Partial<Record<ImportField, number>> = {};
  const taken = new Set<number>();
  for (const { key } of IMPORT_FIELDS) {
    const index = headers.findIndex(
      (header, position) => !taken.has(position) && ALIASES[key].includes(normalise(header)),
    );
    if (index >= 0) {
      mapping[key] = index;
      taken.add(index);
    }
  }
  return mapping;
}

export interface ImportRow {
  readonly row_number: number;
  readonly [field: string]: string | number;
}

/**
 * Turns parsed lines into the objects the staging function takes. Row numbers count from the header as line 1, the
 * way a spreadsheet shows them, so an error reported on row 7 is on row 7 when the church opens its file.
 */
export function toImportRows(
  lines: readonly string[][],
  mapping: Partial<Record<ImportField, number>>,
): ImportRow[] {
  return lines.slice(1).map((cells, index) => {
    const row: Record<string, string | number> = { row_number: index + 2 };
    for (const [field, column] of Object.entries(mapping)) {
      row[field] = (cells[column] ?? "").trim();
    }
    return row as ImportRow;
  });
}

export const IMPORT_LIMITS = { maxBytes: 25 * 1024 * 1024, maxRows: 50_000, chunk: 500 } as const;
